import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sun, Radar, Sparkles, Mail, Eye, Calendar, Users, Flame, Zap, Rocket,
  RefreshCw, CheckCircle2, Lock, Clock, Coins, Activity, MousePointerClick,
  Search, Wand2, X, MessageCircle, Copy, ExternalLink, Loader2, ChevronDown,
} from "lucide-react";
import { authApi } from "./authApi";
import { fmtPrice } from "./api";

const STAT_ICON = {
  signals: Radar, new_prospects: Sparkles, hot_matches: Flame, emails_prepared: Mail,
  emails_sent: Mail, opened: Eye, visit_requests: Calendar, partner_acq: Users,
};
const FEED_ICON = { email: Mail, open: Eye, click: MousePointerClick, match: Sparkles, ai: Wand2, detection: Search };

const CHAN_LABEL = {
  off_markette: "Off Markette", partenaires: "Partenaires", email: "Email", crm: "CRM",
  web: "Web", facebook: "Facebook", whatsapp: "WhatsApp", instagram: "Instagram", communautes: "Communautés",
};

export function EmmaWorkBlock({ setTab, onRescan }) {
  const [d, setD] = useState(null);
  const [showFeed, setShowFeed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const load = useCallback(() => { authApi.emmaWork().then(setD).catch(() => setD({ error: true })); }, []);
  useEffect(() => { load(); }, [load]);
  const rescan = async () => {
    setBusy(true); setToast("");
    try { await load(); onRescan && onRescan(); setToast(`${(d && d.agent_name) || "Emma"} a relancé son analyse ✓`); }
    catch (_) { setToast("Analyse indisponible."); }
    setTimeout(() => setToast(""), 2600); setBusy(false);
  };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch (_) { return ""; } };
  if (!d) return <div className="rounded-2xl bg-white border border-zinc-100 p-6 mb-5"><Loader2 className="w-5 h-5 animate-spin text-[#C19A6B]" /></div>;
  if (d.error) return null;
  const emma = d.agent_name || "Emma";
  const tiles = (d.stats || []).filter((s) => s.key === "signals" || (s.value || 0) > 0);

  return (
    <div className="mb-6 space-y-4" data-testid="emma-work-block">
      {/* Bloc matin */}
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0A0A0A] via-[#141210] to-[#2A2219] p-5 sm:p-7">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-[#C19A6B]/10 blur-2xl" />
        <div className="relative">
          <div className="flex items-center gap-2 text-[#E8D8C3] mb-1">
            <Sun className="w-4 h-4 text-[#C19A6B]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em]">Votre point du matin</span>
          </div>
          <h3 className="text-[#f5f5f5] text-xl sm:text-2xl font-bold leading-tight">
            {d.name ? `Bonjour ${d.name}, ` : "Bonjour, "}<span className="text-[#C19A6B]">{emma} a travaillé pour vous.</span>
          </h3>
          <p className="text-white/65 text-[13px] mt-1">{d.has_work ? `Voici son bilan ${d.since_label}.` : `Aucune nouveauté ${d.since_label} — son radar reste à l'affût.`}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-5" data-testid="emma-stats">
            {tiles.map((s) => { const Ic = STAT_ICON[s.key] || Activity; return (
              <div key={s.key} className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid={`emma-stat-${s.key}`}>
                <div className="flex items-center gap-1.5 text-[#C19A6B] mb-1"><Ic className="w-3.5 h-3.5" /><span className="text-2xl font-extrabold text-white leading-none">{s.value}</span></div>
                <div className="text-[11px] text-zinc-300 leading-tight">{s.label}</div>
                <div className="text-[10px] text-zinc-500 leading-tight mt-0.5">{s.period}</div>
              </div>
            ); })}
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            <button onClick={() => setShowFeed((v) => !v)} data-testid="emma-see-work-btn" className="inline-flex items-center gap-1.5 bg-[#C19A6B] hover:bg-[#A8865C] text-white text-sm font-semibold rounded-lg px-4 py-2 transition-colors">
              <Activity className="w-4 h-4" /> Voir le travail d'{emma} <ChevronDown className={`w-4 h-4 transition-transform ${showFeed ? "rotate-180" : ""}`} />
            </button>
            <button onClick={rescan} disabled={busy} data-testid="emma-run-btn" className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/15 text-white border border-white/15 text-sm font-semibold rounded-lg px-4 py-2 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 text-[#C19A6B]" />} Lancer {emma} maintenant
            </button>
          </div>
          {toast && <p className="text-[12px] text-[#E8D8C3] mt-2" data-testid="emma-toast">{toast}</p>}

          {/* Activité live */}
          <AnimatePresence>
            {showFeed && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                <div className="mt-4 pt-4 border-t border-white/10" data-testid="emma-timeline">
                  {(d.timeline || []).length === 0 ? (
                    <p className="text-[13px] text-zinc-400">Aucune activité récente enregistrée.</p>
                  ) : (
                    <div className="space-y-2.5">
                      {d.timeline.map((t, i) => { const Ic = FEED_ICON[t.type] || Activity; return (
                        <div key={i} className="flex items-start gap-2.5">
                          <span className="w-6 h-6 rounded-full bg-[#C19A6B]/15 flex items-center justify-center shrink-0 mt-0.5"><Ic className="w-3 h-3 text-[#C19A6B]" /></span>
                          <div className="min-w-0"><p className="text-[13px] text-zinc-200 leading-snug">{t.text}</p><p className="text-[10px] text-zinc-500">{fmt(t.at)}</p></div>
                        </div>
                      ); })}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Panneau Radar */}
      <div className="rounded-2xl bg-white border border-zinc-100 p-5" data-testid="emma-radar-panel">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-[#C19A6B]/12 flex items-center justify-center"><Radar className="w-4 h-4 text-[#C19A6B]" /></span>
            <div>
              <p className="text-sm font-bold text-[#111827] leading-none">Radar Acquéreurs</p>
              <p className="text-[11px] text-emerald-600 font-semibold mt-1 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> ACTIF · {d.radar.mode === "auto" ? "envoi automatique" : "validation par vous"}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-zinc-400">Prochaine synthèse</p>
            <p className="text-[13px] font-semibold text-[#111827] inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#C19A6B]" /> {d.radar.next_scan}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
          {(d.radar.channels || []).map((c) => (
            <div key={c.key} className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium ${c.active ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-zinc-50 border-zinc-200 text-zinc-400"}`} data-testid={`radar-chan-${c.key}`}>
              {c.active ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <Lock className="w-3 h-3 shrink-0" />}
              <span className="truncate">{CHAN_LABEL[c.key] || c.label}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-[12px] text-zinc-500">
            <span className="inline-flex items-center gap-1"><Coins className="w-3.5 h-3.5 text-[#C19A6B]" /> {d.radar.credits} crédit{d.radar.credits > 1 ? "s" : ""} Agent IA</span>
            {d.radar.ai_cost_eur > 0 && <span className="inline-flex items-center gap-1">Coût IA cumulé : <b className="text-[#111827]">{d.radar.ai_cost_eur} €</b></span>}
          </div>
          <button onClick={() => setTab && setTab("agent_settings")} data-testid="emma-boost-btn" className="inline-flex items-center gap-1.5 border border-[#C19A6B] bg-[#C19A6B]/10 text-[#C19A6B] hover:bg-[#C19A6B]/20 text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors">
            <Rocket className="w-3.5 h-3.5" /> Booster un bien
          </button>
        </div>
      </div>
    </div>
  );
}

export function WhatsAppDraftModal({ acqId, onClose }) {
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { authApi.whatsappDraft(acqId).then(setD).catch(() => setD({ error: true })); }, [acqId]);
  const copy = async () => {
    const txt = (d && d.message) || "";
    let ok = false;
    try { await navigator.clipboard.writeText(txt); ok = true; }
    catch (_) {
      try { const ta = document.createElement("textarea"); ta.value = txt; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.focus(); ta.select(); ok = document.execCommand("copy"); document.body.removeChild(ta); } catch (e) { ok = false; }
    }
    setCopied(ok ? "ok" : "err"); setTimeout(() => setCopied(false), 2200);
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="whatsapp-draft-modal">
      <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-[12px] shadow-2xl overflow-hidden">
        <div className="px-5 py-4 bg-[#111827] bg-gradient-to-br from-[#0d0d10] to-[#1a1712] flex items-center justify-between">
          <p className="text-white font-semibold flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#C19A6B]" /> Message WhatsApp préparé</p>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {!d ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C19A6B]" /></div> : d.error ? <div className="p-8 text-center text-sm text-zinc-500">Brouillon indisponible.</div> : (
          <div className="p-5 space-y-3">
            {d.listing && (
              <div className="flex items-center gap-3 rounded-xl border border-[#C19A6B]/30 bg-[#C19A6B]/5 p-2.5">
                {d.listing.cover ? <img src={d.listing.cover} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-zinc-100" />}
                <div className="min-w-0"><p className="text-sm font-semibold text-[#111827] truncate">{d.listing.title}</p><p className="text-[11px] text-zinc-500">{d.listing.city} {d.listing.price ? `· ${fmtPrice(d.listing.price)}` : ""}</p></div>
              </div>
            )}
            <textarea readOnly value={d.message} className="w-full h-40 text-[13px] leading-relaxed rounded-lg border border-zinc-200 bg-[#FAFAFA] p-3 outline-none resize-none" data-testid="whatsapp-draft-text" />
            <p className="text-[11px] text-zinc-400 flex items-start gap-1.5"><MessageCircle className="w-3.5 h-3.5 text-[#C19A6B] shrink-0 mt-0.5" /> {d.phone_available ? "Ouvrez WhatsApp pré-rempli, relisez, puis envoyez vous-même." : "Numéro non disponible (RGPD) — copiez le message et envoyez-le depuis votre WhatsApp."}</p>
            <div className="flex gap-2">
              <button onClick={copy} data-testid="whatsapp-copy-btn" className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-[#111827] bg-[#F1F3F5] hover:bg-[#E9ECEF] rounded-lg transition-colors"><Copy className="w-4 h-4" /> {copied === "ok" ? "Copié ✓" : copied === "err" ? "Sélectionnez et copiez" : "Copier le message"}</button>
              {d.wa_url && <a href={d.wa_url} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-open-btn" className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-[#C19A6B] hover:bg-[#A8865C] rounded-lg transition-colors"><ExternalLink className="w-4 h-4" /> Ouvrir WhatsApp</a>}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export function BoostReportPanel({ slug, onOpenProspect }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (slug) authApi.boostReport(slug).then(setD).catch(() => setD({ error: true })); }, [slug]);
  if (!d) return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C19A6B]" /></div>;
  if (d.error) return null;
  const Best = ({ e, label, tone }) => e ? (
    <div className={`rounded-xl border p-3 ${tone === "mine" ? "border-[#C19A6B]/40 bg-[#C19A6B]/5" : "border-zinc-200 bg-white"}`}>
      <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{label}</p>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#111827] truncate">{e.name}{e.partner ? <span className="text-[11px] text-zinc-400 font-normal"> · via {e.partner.short}</span> : ""}</p>
          <p className="text-[11px] text-zinc-500">Source : {e.source_label}{e.recommended_channel ? ` · canal conseillé : ${e.recommended_channel === "whatsapp" ? "WhatsApp" : e.recommended_channel === "email" ? "Email" : e.recommended_channel}` : ""}</p>
        </div>
        <span className="inline-flex items-center gap-1 font-bold text-sm text-[#C19A6B] shrink-0"><Flame className="w-3.5 h-3.5" /> {e.score}%</span>
      </div>
      {onOpenProspect && e.mine && <button onClick={() => onOpenProspect(e.acq_id)} className="mt-2 text-[11px] font-semibold text-[#C19A6B] hover:underline" data-testid="boost-report-open">Voir la fiche</button>}
    </div>
  ) : null;
  return (
    <div className="rounded-2xl border border-[#E8D8C3] bg-[#FDFBF7] p-4 space-y-3" data-testid="boost-report">
      <div className="flex items-center gap-2 text-[#C19A6B]"><Rocket className="w-4 h-4" /><span className="text-[11px] font-semibold uppercase tracking-[0.2em]">Rapport de Boost — {d.title}</span></div>
      <div className="grid grid-cols-3 gap-2">
        {[["scanned", d.scanned, "Signaux analysés"], ["qualified", d.qualified, "Prospects qualifiés"], ["over90", d.over90, "Matchs > 90 %"]].map(([k, v, l]) => (
          <div key={k} className="bg-white rounded-xl border border-[#E8D8C3] p-2.5 text-center" data-testid={`boost-report-${k}`}>
            <div className="text-2xl font-extrabold text-[#C19A6B]">{v}</div>
            <div className="text-[10px] text-zinc-500 leading-tight mt-0.5">{l}</div>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Best e={d.best_mine} label="Meilleur match (à moi)" tone="mine" />
        <Best e={d.best_partner} label="Match partenaire" tone="partner" />
      </div>
      {!d.best_mine && !d.best_partner && <p className="text-[12px] text-zinc-500">Aucun acquéreur compatible détecté pour le moment — le Radar continuera à chercher.</p>}
    </div>
  );
}
